/**
 * Artefact drawer: opens like cart drawer, cycles slides on "next" (loop).
 * Mobile (viewport ≤1023px): horizontal swipe on full panel (.artefact-drawer__inner) to change slides.
 * Depends on global.js (trapFocus, removeTrapFocus).
 */
class ArtefactDrawer extends HTMLElement {
  connectedCallback() {
    this.overlay = this.querySelector('.artefact-drawer__overlay');
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }
    this.addEventListener('keyup', (evt) => {
      if (evt.code === 'Escape') this.close();
    });

    this.querySelectorAll('[data-artefact-close]').forEach((btn) => {
      btn.addEventListener('click', () => this.close());
    });

    this.slides = this.querySelectorAll('[data-artefact-slide]');
    this.currentIndex = 0;
    this._syncSlideA11y();

    const slidesRoot = this.querySelector('[data-artefact-slides]');
    if (slidesRoot) {
      slidesRoot.addEventListener('click', (e) => {
        const nextBtn = e.target.closest('[data-artefact-next]');
        if (nextBtn) {
          e.preventDefault();
          this.goNext();
        }
      });
    }

    /** Mobile: swipe on full drawer (scrollable inner), not only the slides grid */
    const drawerPanel = this.querySelector('.artefact-drawer__inner');
    if (drawerPanel) {
      let swipeStartX = 0;
      let swipeStartY = 0;
      drawerPanel.addEventListener(
        'touchstart',
        (e) => {
          if (e.changedTouches.length !== 1) return;
          swipeStartX = e.changedTouches[0].clientX;
          swipeStartY = e.changedTouches[0].clientY;
        },
        { passive: true }
      );
      drawerPanel.addEventListener(
        'touchend',
        (e) => {
          if (!this._artefactSwipeEnabled() || e.changedTouches.length !== 1) return;
          const x = e.changedTouches[0].clientX;
          const y = e.changedTouches[0].clientY;
          const dx = x - swipeStartX;
          const dy = y - swipeStartY;
          const threshold = 48;
          if (Math.abs(dx) < threshold || Math.abs(dx) < Math.abs(dy)) return;
          if (dx < 0) this.goNext();
          else this.goPrevious();
        },
        { passive: true }
      );
    }

    const productId = this.dataset.productId;
    if (productId) {
      document.querySelectorAll(`[data-artefact-open="${productId}"]`).forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          if (this.classList.contains('active')) {
            this.close();
          } else {
            this.open(btn);
          }
        });
      });
    }
  }

  open(triggeredBy) {
    if (triggeredBy) this.activeElement = triggeredBy;
    this.currentIndex = 0;
    if (this.slides && this.slides.length) {
      this.slides.forEach((slide, i) => {
        slide.classList.toggle('is-active', i === 0);
      });
      this._syncSlideA11y();
    }
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const shell = this.querySelector('.artefact-drawer__shell');
        const inner = this.querySelector('.drawer__inner');
        if (typeof trapFocus === 'function' && shell && inner) {
          trapFocus(shell, inner);
        }
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden', 'artefact-drawer-open');
  }

  close() {
    this.classList.remove('active', 'animate');
    document.body.classList.remove('overflow-hidden', 'artefact-drawer-open');
    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.activeElement);
    }
  }

  goNext() {
    if (!this.slides || this.slides.length < 2) return;
    const outgoing = this.slides[this.currentIndex];
    const nextIndex = (this.currentIndex + 1) % this.slides.length;
    const incoming = this.slides[nextIndex];

    outgoing.classList.remove('is-active');
    incoming.classList.add('is-active');
    this.currentIndex = nextIndex;
    this._syncSlideA11y();
  }

  goPrevious() {
    if (!this.slides || this.slides.length < 2) return;
    const outgoing = this.slides[this.currentIndex];
    const prevIndex = (this.currentIndex - 1 + this.slides.length) % this.slides.length;
    const incoming = this.slides[prevIndex];

    outgoing.classList.remove('is-active');
    incoming.classList.add('is-active');
    this.currentIndex = prevIndex;
    this._syncSlideA11y();
  }

  _artefactSwipeEnabled() {
    return (
      this.slides &&
      this.slides.length > 1 &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(max-width: 1023px)').matches
    );
  }

  /** aria-hidden + inert so only the visible slide is in the tab order */
  _syncSlideA11y() {
    if (!this.slides || !this.slides.length) return;
    this.slides.forEach((slide, i) => {
      const active = i === this.currentIndex;
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      if ('inert' in slide) slide.inert = !active;
    });
  }
}

if (!customElements.get('artefact-drawer')) {
  customElements.define('artefact-drawer', ArtefactDrawer);
}
